import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { RadioGroup, RadioGroupItem } from './ui/radio-group';
import { Minus, Plus } from 'lucide-react';

interface QuantitySelectionDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (quantity: number) => void;
  totalCount: number;
  actionType: 'sell' | 'grading';
  cardName?: string;
}

export const QuantitySelectionDialog: React.FC<QuantitySelectionDialogProps> = ({
  open,
  onClose,
  onConfirm,
  totalCount,
  actionType,
  cardName,
}) => {
  const [selectionMode, setSelectionMode] = useState<'all' | 'some'>('all');
  const [quantity, setQuantity] = useState<number>(1);

  useEffect(() => {
    if (open) {
      setSelectionMode('all');
      setQuantity(1); // Always reset to 1 when dialog opens
    }
  }, [open, totalCount]); // Also reset when totalCount changes (new dialog for different group)

  const handleConfirm = () => {
    const finalQuantity = selectionMode === 'all' ? totalCount : quantity;
    onConfirm(finalQuantity);
    onClose();
  };

  const handleIncrement = () => {
    if (quantity < totalCount) {
      setQuantity(prev => prev + 1);
    }
  };

  const handleDecrement = () => {
    if (quantity > 1) {
      setQuantity(prev => prev - 1);
    }
  };

  const handleQuantityChange = (value: string) => {
    const numValue = parseInt(value, 10);
    if (!isNaN(numValue) && numValue >= 1 && numValue <= totalCount) {
      setQuantity(numValue);
    } else if (value === '') {
      setQuantity(1);
    }
  };

  const actionLabel = actionType === 'sell' ? 'Mark as Sold' : 'Move to Grading';
  const actionLabelLower = actionType === 'sell' ? 'mark as sold' : 'move to grading';

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>{actionLabel} - Quantity Selection</DialogTitle>
          <DialogDescription>
            {cardName && (
              <span className="block mb-2 font-medium">{cardName} (x{totalCount})</span>
            )}
            Select how many cards you want to {actionLabelLower}.
          </DialogDescription>
        </DialogHeader>
        
        <div className="py-4 space-y-4">
          <RadioGroup value={selectionMode} onValueChange={(value) => setSelectionMode(value as 'all' | 'some')}>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="all" id="all" />
              <Label htmlFor="all" className="font-normal cursor-pointer">
                All cards ({totalCount})
              </Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="some" id="some" />
              <Label htmlFor="some" className="font-normal cursor-pointer">
                Some cards
              </Label>
            </div>
          </RadioGroup>

          {selectionMode === 'some' && (
            <div className="pl-6 space-y-2">
              <Label htmlFor="quantity">Number of cards</Label>
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  onClick={handleDecrement}
                  disabled={quantity <= 1}
                  className="h-9 w-9"
                >
                  <Minus className="h-4 w-4" />
                </Button>
                <Input
                  id="quantity"
                  type="number"
                  min={1}
                  max={totalCount}
                  value={quantity}
                  onChange={(e) => handleQuantityChange(e.target.value)}
                  className="w-20 text-center"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  onClick={handleIncrement}
                  disabled={quantity >= totalCount}
                  className="h-9 w-9"
                >
                  <Plus className="h-4 w-4" />
                </Button>
                <span className="text-sm text-muted-foreground">
                  of {totalCount}
                </span>
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleConfirm}>
            Continue
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};


